import React, { useRef } from 'react'
import { Formik } from 'formik'
import { TableEventListeners } from 'antd/lib/table'
import { useDispatch } from 'react-redux'
import { Dropdown, Switch } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Drawer,
  Menu,
  MenuItem,
  Text,
  ToolIcon
} from 'src/components/Ant'
import { ColumnProps } from 'src/components/Ant/Table'
import { Id } from 'src/types'
import { sendBedrooms } from 'src/core/store/ducks/partners/bedrooms/actions'

import { initialValues } from './initial-values'
import * as CSS from './styles'
import validationSchema from './validation-schema'

import { SideModal } from '../sideModal'

export interface DataSource {
  id: Id
  category: string
  comments: string
  max_adults: number
  active: Boolean
}

interface Props {
  agency_partner_id: Id
  currentPage: number
  dataSource: DataSource[]
  hotel_id: Id
  isNew: boolean
  loading: boolean
  onChange(page: number): void
  onRow?: (record: DataSource, index: number) => TableEventListeners
  openSideModal?: boolean
  partner_id: Id
  setIsNew(isNew: boolean): void
  setOpenSideModal?: (openSideModal: boolean) => void
  setOpenSideModalOffer?: (openSideModalOffer: boolean) => void
  setRoomId?: (roomId: Id) => void
  totalPages: number
}

export default function Table({
  agency_partner_id,
  currentPage,
  dataSource,
  hotel_id,
  loading,
  onChange,
  openSideModal,
  partner_id,
  setIsNew,
  setOpenSideModal,
  setOpenSideModalOffer,
  setRoomId,
  totalPages
}: Props) {
  const refFormik = useRef(null)
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const columns: ColumnProps<unknown>[] = [
    {
      title: 'web.table.bedrooms.id',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'web.table.bedrooms.category.name',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'web.table.bedrooms.capacity.name',
      dataIndex: 'max_adults',
      key: 'max_adults'
    },
    {
      title: 'web.table.bedrooms.status',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: any) => (
        <Switch
          checked={active}
          checkedChildren={t('common.active')}
          onChange={value => {
            sendBedroomsData({ ...record, active: value })
          }}
          style={{ width: '5rem' }}
          unCheckedChildren={t('common.inactive')}
        />
      )
    },
    {
      dataIndex: 'actions',
      render: (_: any, record: any) => {
        return (
          <Dropdown
            overlay={
              <Menu
                onClick={async ({ key }: any) => {
                  switch (key) {
                    case 'add-offer':
                      setRoomId(record.id)
                      setOpenSideModalOffer(true)
                      break
                  }
                }}
              >
                <MenuItem
                  key="add-offer"
                  title="web.table.bedrooms.see-offers"
                />
              </Menu>
            }
            placement="bottomRight"
          >
            <Button type="link">
              <ToolIcon type="more" style={{ fontWeight: 'bold' }} />
            </Button>
          </Dropdown>
        )
      },
      width: 50
    }
  ]

  function sendBedroomsData(values: any) {
    dispatch(
      sendBedrooms([
        {
          ...values,
          partner_hotel_room_id: values.id,
          status: values.active,
          partner_id,
          partner_hotel_id: hotel_id
        }
      ])
    )
  }

  return (
    <>
      <CSS.Table
        className="table-striped"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        onChange={({ current }) => onChange(current)}
        pagination={{
          current: currentPage,
          pageSize: 10,
          total: totalPages
        }}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={() => {}}
        ref={refFormik}
        render={formikProps => {
          const { id } = formikProps.values
          const isNew = id
          return (
            <Drawer
              footer={
                <CSS.Div>
                  <div></div>
                  <div>
                    <Button
                      onClick={async () => {
                        setOpenSideModal(false)
                        formikProps.resetForm(initialValues)
                      }}
                      type="default"
                      title="common.actions.cancel"
                    />
                    <CSS.Button
                      type="primary"
                      onClick={async () => {
                        const validateReturn = await formikProps.validateForm()

                        if (Object.keys(validateReturn).length === 0) {
                          setOpenSideModal(false)
                          formikProps.resetForm(initialValues)
                          sendBedroomsData(formikProps.values)
                        }
                      }}
                      title={
                        !isNew ? 'common.actions.save' : 'common.actions.change'
                      }
                    />
                  </div>
                </CSS.Div>
                // end footer **************************
              }
              onClose={() => {
                setOpenSideModal(false)
                formikProps.resetForm(initialValues)
              }}
              title={
                <Text
                  title={
                    !isNew ? 'common.actions.add' : 'common.actions.change'
                  }
                />
              }
              visible={openSideModal}
              width="40%"
            >
              <SideModal isNew={isNew} formikProps={formikProps} />
            </Drawer>
          )
        }}
        validationSchema={validationSchema}
      />
    </>
  )
}
